import React, { ChangeEvent, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import uniqueId from 'lodash/uniqueId'

import { CircularProgress, Tooltip } from '@material-ui/core'

import { IOSSwitch } from './IOSSwitch'

import { useLogic } from '../hooks/storeHook'
import { useBoolState } from '../hooks/commonHooks'
import { useMessages } from '../hooks/snackbarHooks'

import { AccountModelBase } from '../server/mpsklad_core/Models/AccountModelBase'

export type AccountSyncSwitchProps<TAccount extends AccountModelBase> = {
  account: TAccount

  setSync: (syncEnabled: boolean, accountId: number) => Promise<void>

  disabled?: boolean

  readOnly?: boolean
}

export const AccountSyncSwitch =
  observer(
    <TAccount extends AccountModelBase>
    ({account, setSync, disabled, readOnly}: AccountSyncSwitchProps<TAccount>) => {
      const {confirmSyncEnable, confirmSyncDisable} = useLogic()

      const {showSuccess, showError} = useMessages()
      const [isSubmitting, setSubmitting, setFinished] = useBoolState()

      const switchName = useRef(uniqueId('StoreAccountSyncSwitch_')).current

      const onSyncChange =
        async ({target: {checked: syncEnabled}}: ChangeEvent<HTMLInputElement>) => {
          if (disabled || readOnly) {
            return
          }

          if (syncEnabled && !await confirmSyncEnable()) {
            return
          }

          if (!syncEnabled && !await confirmSyncDisable()) {
            return
          }

          setSubmitting()

          try {
            await setSync(syncEnabled, account.id)
            showSuccess(`Синхронизация ${syncEnabled ? 'включена' : 'отключена'}`)
          } catch (e) {
            console.log('Failed to toggle store sync', e)
            showError('Ошибка при сохранении')
          } finally {
            setFinished()
          }
        }

      const tooltip = readOnly || disabled
                      ? `Синхронизация ${account.syncEnabled ? 'включена' : 'отключена'}`
                      : `${account.syncEnabled ? 'Отключить' : 'Включить'} синхронизацию...`

      return isSubmitting
             ? <CircularProgress size={19} title="Переключаем..."/>
             :
             <Tooltip
               arrow
               placement="bottom"
               title={tooltip}>
               <IOSSwitch
                 color="primary"
                 name={switchName}
                 disabled={disabled}
                 readOnly={readOnly}
                 checked={account.syncEnabled}
                 onChange={onSyncChange}
                 onClick={e => e.stopPropagation() /* prevent tab switching */}
               />
             </Tooltip>
    })